
import { useMemo, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { btn, cx, inputSm, notice } from '@/lib/ui'
import { SIGNAL_META } from '@/lib/signals'
import { handleIdFor, nodeDisplayName, splitPorts, type PortDTO } from '@/lib/types'
import type { DeviceFlowNode } from './types'

/**
 * Patching a cable without a pointer.
 *
 * On the canvas a cable is made by dragging between two handles: 10px divs
 * that React Flow renders with no tabindex and no role, so they cannot be
 * reached by keyboard at all. That made the app's central action — joining two
 * pieces of equipment — mouse-only, and nothing else in the interface could do
 * it. This is the same operation as three selects and a button, validated by
 * exactly the same rules the drag uses.
 */
export function PatchForm({
  node,
  nodes,
  onPatch,
  validate,
}: {
  node: DeviceFlowNode
  nodes: DeviceFlowNode[]
  /** Handles, not port ids: the same shape onConnect receives from a drag. */
  onPatch: (sourceHandle: string, targetNodeId: string, targetHandle: string) => void
  validate: (
    sourceNodeId: string,
    sourceHandle: string,
    targetNodeId: string,
    targetHandle: string,
  ) => { ok: true } | { ok: false; reason: string | null }
}) {
  const [sourceHandle, setSourceHandle] = useState('')
  const [targetNodeId, setTargetNodeId] = useState('')
  const [targetHandle, setTargetHandle] = useState('')

  const outs = useMemo(() => {
    const { outputs, bidirectional } = splitPorts(node.data.device.ports)
    return [
      ...outputs.map((port) => ({ port, handle: port.id })),
      ...bidirectional.map((port) => ({ port, handle: handleIdFor(port, 'source') })),
    ]
  }, [node])

  const others = useMemo(() => nodes.filter((other) => other.id !== node.id), [nodes, node.id])
  const target = others.find((other) => other.id === targetNodeId)

  const ins = useMemo(() => {
    if (!target) return []
    const { inputs, bidirectional } = splitPorts(target.data.device.ports)
    return [
      ...inputs.map((port) => ({ port, handle: port.id })),
      ...bidirectional.map((port) => ({ port, handle: handleIdFor(port, 'target') })),
    ]
  }, [target])

  const ready = Boolean(sourceHandle && targetNodeId && targetHandle)
  const verdict = ready ? validate(node.id, sourceHandle, targetNodeId, targetHandle) : null
  const label = (port: PortDTO) => `${port.name} · ${SIGNAL_META[port.signal].label}`
  const nodeName = (candidate: DeviceFlowNode) =>
    nodeDisplayName(candidate.data.label, candidate.data.device.name)

  if (outs.length === 0) return null

  return (
    <div>
      <h3 className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Patch a cable</h3>
      <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
        The same as dragging between two ports, for when dragging is not an option.
      </p>

      <label className="sr-only" htmlFor="patch-from">
        From this device&rsquo;s port
      </label>
      <select
        id="patch-from"
        className={cx(inputSm, 'mt-2 w-full')}
        value={sourceHandle}
        onChange={(event) => setSourceHandle(event.target.value)}
      >
        <option value="">From port…</option>
        {outs.map(({ port, handle }) => (
          <option key={handle} value={handle}>
            {label(port)}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="patch-device">
        To which device
      </label>
      <select
        id="patch-device"
        className={cx(inputSm, 'mt-1.5 w-full')}
        value={targetNodeId}
        onChange={(event) => {
          setTargetNodeId(event.target.value)
          setTargetHandle('')
        }}
      >
        <option value="">To device…</option>
        {others.map((other) => (
          <option key={other.id} value={other.id}>
            {nodeName(other)}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="patch-to">
        To which port
      </label>
      <select
        id="patch-to"
        className={cx(inputSm, 'mt-1.5 w-full')}
        value={targetHandle}
        disabled={!target}
        onChange={(event) => setTargetHandle(event.target.value)}
      >
        <option value="">To port…</option>
        {ins.map(({ port, handle }) => (
          <option key={handle} value={handle}>
            {label(port)}
          </option>
        ))}
      </select>

      {verdict && !verdict.ok && verdict.reason && (
        <p role="alert" className={cx('mt-2', notice.error)}>
          {verdict.reason}
        </p>
      )}

      <button
        type="button"
        disabled={!ready || !verdict?.ok}
        onClick={() => {
          onPatch(sourceHandle, targetNodeId, targetHandle)
          setSourceHandle('')
          setTargetNodeId('')
          setTargetHandle('')
        }}
        className={cx(btn.sm, btn.primary, 'mt-2 w-full justify-center')}
      >
        <Icon name="cable" className="h-3.5 w-3.5" />
        Patch
      </button>
    </div>
  )
}
